import type { GoogleAdsAdGroup, IGoogleAdsAdText } from '@feathr/blackbox';
import { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';

/** Broad reaches the most searches, and can be narrowed per keyword afterwards. */
export const DEFAULT_KEYWORD_MATCH_TYPE = EGoogleAdsKeywordMatchType.BROAD;

/** A positive keyword within an ad group, paired with its match type. */
export interface IAdGroupKeyword {
  /** The row's stable id. Round-trips to the API, which uses it to match Google criteria. */
  key: string;
  text: string;
  matchType: EGoogleAdsKeywordMatchType;
}

/** A sitelink, shared by every ad in the group rather than owned by one ad. */
export interface ISitelink {
  key: string;
  /** The clickable link label shown beneath the ad. */
  text: string;
  url: string;
  /** No UI, but round-tripped so an edit doesn't wipe descriptions the API already has. */
  description1?: string | null;
  description2?: string | null;
}

/** A single responsive search ad. */
export interface IAd {
  /** The creative's id. */
  key: string;
  headlines: IGoogleAdsAdText[];
  descriptions: IGoogleAdsAdText[];
  /** Landing page for this ad specifically — Search sets it per ad, not per campaign. */
  finalUrl: string;
}

/** An ad group: a set of keywords and sitelinks shared by the ads it contains. */
export interface IAdGroup {
  /** The ad group's id. */
  key: string;
  name: string;
  keywords: IAdGroupKeyword[];
  /** Terms that suppress the ad. Negative keywords carry no match type. */
  negativeKeywords: IAdGroupKeyword[];
  sitelinks: ISitelink[];
  ads: IAd[];
}

/** The subset of an ad group the editor can patch. Ads go through their own mutators. */
export type TAdGroupPatch = Partial<
  Pick<IAdGroup, 'keywords' | 'name' | 'negativeKeywords' | 'sitelinks'>
>;

/** Messages for one sitelink row, split by the field each belongs against. */
export interface ISitelinkRowErrors {
  text: string[];
  url: string[];
}

/** Messages for one ad, keyed by the row they belong against. */
export interface IAdRowErrors {
  headlines: Record<string, string[]>;
  descriptions: Record<string, string[]>;
  finalUrl: string[];
}

/**
 * Every message for one ad group that has a field to sit against.
 *
 * Keyed by row key rather than index. The model holds one keyword list that the UI splits
 * in two, and its ads are separate models, so an index into either would mean nothing
 * here — the keys survive both.
 */
export interface IAdGroupRowErrors {
  name: string[];
  /** Covers both keyword editors, which share one list on the model. */
  keywords: Record<string, string[]>;
  sitelinks: Record<string, ISitelinkRowErrors>;
  /** Keyed by ad key, which is the creative's id. */
  ads: Record<string, IAdRowErrors>;
}

export interface IValidateAdStepResult {
  /** Per-row messages, index-aligned with the ad group array. */
  rowErrors: IAdGroupRowErrors[];
  /**
   * Ad-group-level messages, index-aligned with the ad group array. These are the ones
   * with no single field to sit against — counts and minimums.
   */
  adGroupErrors: string[][];
  /**
   * Every ad group index with at least one problem. Drives each card's
   * complete/incomplete header icon and which card auto-opens.
   */
  invalidAdGroupIndexes: number[];
  /**
   * Publish-gating aggregate: the campaign-wide ad group count plus one entry per invalid
   * ad group. Campaign-wide messages never flag an individual card.
   */
  adsStepErrors: string[];
}

/**
 * The ad group list plus its mutators. Owned by {@link useAdGroups} and threaded through
 * the Ads step so the wizard can validate the same data it renders. Views are derived
 * from the models every render, so the mutators write through rather than return state.
 */
export interface IAdGroupsController {
  adGroups: IAdGroup[];
  /**
   * Everything wrong with the current ad groups. Derived from the models' own constraints
   * plus the Ad Grant rules no single model can express, so the step and the wizard read
   * one result rather than validating separately.
   */
  validation: IValidateAdStepResult;
  /**
   * False until the ad group list has come back, so the step can hold off its empty
   * state. A failed load counts as loaded.
   */
  isLoaded: boolean;
  /** Set when the ad groups couldn't be fetched, so the step can say so. */
  loadError: Error | null;
  /**
   * Creates the ad group server-side straight away — its ads need a real id to exist.
   *
   * Resolves to the index it landed at, or null if it was never created. Callers must not
   * assume the list grew: opening an index that doesn't exist renders no editor while
   * locking every other card, leaving no `Done` button to escape with.
   */
  addAdGroup: () => Promise<number | null>;
  /** Archives the group's ads first — the API refuses to delete a group that still has any. */
  removeAdGroup: (index: number) => Promise<void>;
  renameAdGroup: (index: number, name: string) => void;
  updateAdGroup: (index: number, patch: TAdGroupPatch) => void;
  addAd: (index: number) => void;
  removeAd: (index: number, adIndex: number) => void;
  updateAd: (index: number, adIndex: number, patch: Partial<Omit<IAd, 'key'>>) => void;
  /** Persists an ad group and its ads. Called when the user finishes editing one. */
  saveAdGroup: (index: number) => Promise<void>;
}

/**
 * What {@link useAdGroups} returns. The step gets the controller; the wizard also needs
 * the models themselves, to hand to `SaveCampaignButton`.
 */
export interface IUseAdGroupsResult extends IAdGroupsController {
  /**
   * The ad group models. These go in `grandchildModels` rather than `childModels` so
   * publishing saves every ad group before any ad, which is the order the API requires.
   */
  adGroupModels: GoogleAdsAdGroup[];
  /**
   * Whether the ad group fetch has come back — loaded or failed.
   *
   * Not `!isPending`: a list that has not started fetching is not pending either, so
   * gating on that lets the wizard decide which step to land on while the collections are
   * still empty. A failed fetch counts, or the wizard would never initialize at all.
   *
   * A function rather than a boolean, because the wizard hands this to `useWizardState`'s
   * `waitFor`, which runs inside a MobX reaction that only tracks what the function reads
   * when *it* calls it. A boolean computed at render time would freeze at its first value.
   * See `IWizardUseSteps.waitFor`.
   */
  hasLoaded: () => boolean;
}
